import { ShowContextProvider, Title, useEditController } from "react-admin";
import { useParams } from "react-router-dom";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { IdChip } from "../../components/IdChip";
import { showLabelSx } from "../../components/listStyles";
import { colors, fontFamilyMono } from "../../data/tokens";
import { roleLabel } from "../../util/roleLabel";
import {
  PROFILE_PRIORITY_NOTE,
  ProfileChip,
  asPermissionList,
  isReservedRole,
  permissionInfo,
  useRoleUserCount,
} from "./userRoleMeta";

const InfoItem = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box>
    <Typography sx={showLabelSx}>{label}</Typography>
    <Box sx={{ marginTop: 0.5 }}>{children}</Box>
  </Box>
);

// Ficha no padrão do painel. A versão antiga mostrava id, nome e o JSON
// cru das permissões ("ADMIN_MANAGE_ANY_ANYTHING").
export const UserRolesShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "user-roles/crud",
    id,
  });
  const record = controller?.record;
  const { count } = useRoleUserCount(record?.id);
  const permissions = asPermissionList(record?.permissions);
  const usersLink = record
    ? `#/users/crud?filter=${encodeURIComponent(JSON.stringify({ roleId: record.id }))}`
    : "#";

  return (
    <ShowContextProvider value={controller}>
      <Title title="Regras de Usuário" />
      <PageTopBreadcrumb
        title="Regras de Usuário"
        firstPath="/#/user-roles/crud"
        secondTitle="Visualizando"
        thirdTitle={record ? roleLabel(record.name) : undefined}
        actionType="edit"
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.primaryDark }}>
                {roleLabel(record.name)}
              </Typography>
              <ProfileChip permissions={record.permissions} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: colors.textMuted, marginTop: 0.5 }}>
              Nome gravado no sistema:{" "}
              <Box component="span" sx={{ fontFamily: fontFamilyMono, color: colors.textStrong }}>
                {record.name}
              </Box>
              {isReservedRole(record.name) &&
                " — usado pelo servidor; não pode ser renomeado nem excluído"}
            </Typography>

            <Divider sx={{ marginY: 2.5 }} />

            <InfoItem label="Permissões">
              {permissions.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                  Nenhuma — quem tem esta regra fica sem perfil no painel
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {permissions.map((code) => {
                    const info = permissionInfo(code);
                    return (
                      <Box key={code} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip size="small" label={info ? info.name : code} />
                        <Typography sx={{ fontSize: 13, color: colors.textStrong }}>
                          {info ? info.effect : "Permissão desconhecida pelo painel"}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFamily: fontFamilyMono }}>
                          {code}
                        </Typography>
                      </Box>
                    );
                  })}
                  {permissions.length > 1 && (
                    <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                      {PROFILE_PRIORITY_NOTE}
                    </Typography>
                  )}
                </Box>
              )}
            </InfoItem>

            <Divider sx={{ marginY: 2.5 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "flex-start" }}>
              <InfoItem label="Usuários com esta regra">
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.textStrong }}>
                    {count === undefined ? "…" : count.toLocaleString("pt-BR")}
                  </Typography>
                  {!!count && (
                    <Link href={usersLink} underline="hover" sx={{ fontSize: 13 }}>
                      Ver na lista de Sócios
                    </Link>
                  )}
                </Box>
              </InfoItem>
              <InfoItem label="Identificador técnico">
                <IdChip value={record.id} />
              </InfoItem>
            </Box>
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
